import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { apiManager } from "../../services/apiManager";
import { outpatientAPI, clinicAppointmentsAPI, inpatientAPI, handleApiError } from "../../services/api";
import { showErrorToast, showSuccessToast } from "../../utils/notificationUtils";
import { USER_TYPES } from "../../constants/userTypes";
import AdmitOutpatientModal from "../modals/AdmitOutpatientModal";
import PatientTypeChooserModal from "../modals/PatientTypeChooserModal";
import NewPatientWizardModal from "../modals/NewPatientWizardModal";
import EditOutpatientModal from "../modals/EditOutpatientModal";
import ConvertToInpatientModal from "../modals/ConvertToInpatientModal";
import CloseOutpatientModal from "../modals/CloseOutpatientModal";
import OutpatientDetailsOverlay from "./OutpatientDetailsOverlay";
import "../../pages/reception/ReceptionStyles.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useTranslation } from "react-i18next";
import { isClinicOnFreePlan } from "../../utils/clinicPlanUtils";

const ROWS_PER_PAGE = 8;

const OutpatientTab = ({
  clinicData,
  clinicDataFromHook,
  userType,
  vets,
  loadingLists,
  outpatientVisits = [],
  inpatientVisits = [],
  fetchAllTabData,
  onDataUpdate,
  onDetailsOpenChange
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();

  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ar" ? "ar-EG" : "en-GB";
  const fallbackLabels = useMemo(
    () => ({
      petGuardian: t("receptionOutpatients.tab.fallback.petGuardian"),
      pet: t("receptionOutpatients.tab.fallback.pet"),
      vet: t("receptionOutpatients.tab.fallback.vet"),
    }),
    [t]
  );
  const formatStatus = useCallback(
    (status) => {
      if (!status) return t("receptionOutpatients.tab.status.unknown");
      const key = status.toLowerCase();
      return t(`receptionOutpatients.tab.status.${key}`, { defaultValue: status });
    },
    [t]
  );

  // Helper function to resolve pet profile image URL
  const getPetImageUrl = useCallback((row) => {
    // row.pet might be a string (pet name) or an object
    // Check originalData.pet first (from API), then row.pet (from MainReceptionPage)
    const pet = row.originalData?.pet || (typeof row.pet === 'object' ? row.pet : null);
    if (!pet || typeof pet === 'string') return "/images/no-image-pet.png";

    // Handle profile_image_path which might be a string or an object
    const profilePath = typeof pet.profile_image_path === "string"
      ? pet.profile_image_path
      : pet.profile_image_path?.url || pet.profile_image_path?.path;

    // Try multiple possible image fields
    const imageUrl =
      pet.primaryImageUrl ||
      pet.primary_image_url ||
      profilePath ||
      pet.profile_image_url ||
      pet.profileImageUrl ||
      pet.image ||
      pet.avatar;

    // If we have a URL, check if it needs the API base URL
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim()) {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      // Check if it's already a full URL (from MainReceptionPage processing)
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      // If it's a relative path, prepend API URL
      // Handle paths with or without leading slash
      const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
      return `${API_BASE_URL}${cleanPath}`;
    }

    return "/images/no-image-pet.png";
  }, []);
  const formatReason = useCallback(
    (reason, vetName) => {
      if (reason && reason.trim()) {
        return reason;
      }
      return t("receptionOutpatients.tab.cards.reasonFallback", {
        vet: vetName || fallbackLabels.vet,
      });
    },
    [t, fallbackLabels.vet]
  );
  const getDefaultDateString = useCallback(() => {
    return new Date()
      .toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" })
      .replace(/\//g, "-");
  }, [locale]);

  const [localOutpatientVisits, setLocalOutpatientVisits] = useState(outpatientVisits);
  const [showAdmitOutpatientModal, setShowAdmitOutpatientModal] = useState(false);
  const [showEditOutpatientModal, setShowEditOutpatientModal] = useState(false);
  const [editOutpatientData, setEditOutpatientData] = useState(null);
  const [admitOutpatientDownpayment, setAdmitOutpatientDownpayment] = useState(0.0);
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
  const [rowToClose, setRowToClose] = useState(null);
  const [convertingVisitId, setConvertingVisitId] = useState(null);
  const [showConvertConfirmModal, setShowConvertConfirmModal] = useState(false);
  const [rowToConvert, setRowToConvert] = useState(null);
  const [page, setPage] = useState(1);

  // Patient chooser / new patient wizard flow
  const [showPatientChooser, setShowPatientChooser] = useState(false);
  const [showNewPatientWizard, setShowNewPatientWizard] = useState(false);
  const [prefillPetOwner, setPrefillPetOwner] = useState(null);
  const [prefillPet, setPrefillPet] = useState(null);
  // Price should come from the latest data in the source array, not stale state
  const rowToCloseFromSource = useMemo(() => {
    if (!rowToClose) return null;
    const sourceArr = (Array.isArray(localOutpatientVisits) && localOutpatientVisits.length)
      ? localOutpatientVisits
      : (outpatientVisits || []);
    return sourceArr.find(v => v.id === rowToClose.id) || rowToClose;
  }, [rowToClose, localOutpatientVisits, outpatientVisits]);
  const [search, setSearch] = useState(() => ({
    // Basic search fields
    date: getDefaultDateString(), // Current date in DD-MM-YYYY format
    pet_owner: "",
    pet: "",
    vet: "",

    // Advanced search parameters
    user_id: "",
    vet_id: "",
    clinic_id: "",
    pet_id: "",
    pet_owner_id: "",
    appointment_type_id: "",
    status: "",
    date_from: "",
    date_to: "",
    datetime_from: "",
    datetime_to: "",
    time_from: "",
    time_to: "",
    search: "",
    user_name: "",
    vet_name: "",
    pet_name: "",
    clinic_name: "",
    canceled_by: "",
    restored_by: "",
    has_canceled_reason: false,
    today: false,
    tomorrow: false,
    this_week: false,
    this_month: false,
    upcoming: false,
    past: false,
    sort_by: "appointment_at",
    sort_order: "desc",
    per_page: 5,
    showAllActive: true,
    showAllCheckout: false,
    showAllClosed: false
  }));


  // Help tooltip state
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [helpTooltipPosition, setHelpTooltipPosition] = useState({ x: 0, y: 0 });

  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [searchPagination, setSearchPagination] = useState(null);

  // Overlay state for detailed view
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedOutpatientId, setSelectedOutpatientId] = useState(null);


  // Refs for Flatpickr
  const searchDateInputRef = useRef(null);
  const searchDatePickerRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setLocalOutpatientVisits(outpatientVisits);
  }, [outpatientVisits]);

  // Check for outpatient visit ID to highlight from sessionStorage (from notification click)
  useEffect(() => {
    const highlightVisitId = sessionStorage.getItem('highlightOutpatientVisitId');
    if (highlightVisitId && localOutpatientVisits.length > 0) {
      // Find the visit to highlight
      const visitToHighlight = localOutpatientVisits.find(
        visit => visit.id?.toString() === highlightVisitId.toString()
      );

      if (visitToHighlight) {
        // Set selected visit to highlight it
        setSelectedOutpatientId(visitToHighlight.id);

        // Scroll to the highlighted visit card after a short delay
        setTimeout(() => {
          const cardElement = document.querySelector(`[data-card-id="${visitToHighlight.id}"]`);
          if (cardElement) {
            cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a temporary highlight animation
            cardElement.style.transition = 'box-shadow 0.3s ease, transform 0.3s ease';
            cardElement.style.boxShadow = '0 0 0 3px #fef3c7, 0 4px 12px rgba(0, 0, 0, 0.15)';
            cardElement.style.transform = 'scale(1.02)';
            setTimeout(() => {
              cardElement.style.boxShadow = '';
              cardElement.style.transform = '';
            }, 2000);
          }
        }, 500);

        // Clear the sessionStorage after highlighting
        sessionStorage.removeItem('highlightOutpatientVisitId');
      }
    }
  }, [localOutpatientVisits]);

  // Use environment variable for backend API base URL
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

  // Get clinic data based on user type
  const clinicDataToUse = userType === USER_TYPES.CLINIC_MODERATOR
    ? (clinicDataFromHook || clinicData)
    : clinicData;

  // Check if clinic is on free plan
  const isFreePlan = isClinicOnFreePlan(clinicDataToUse);;

  // Initialize Flatpickr for search date input
  useEffect(() => {
    if (searchDateInputRef.current && !searchDatePickerRef.current) {
      import("flatpickr").then((flatpickr) => {
        searchDatePickerRef.current = flatpickr.default(searchDateInputRef.current, {
          dateFormat: "d-m-Y",
          allowInput: true,
          clickOpens: true,
          defaultDate: new Date(), // Set current date as default
          onChange: function (selectedDates, dateStr) {
            setSearch((s) => ({ ...s, date: dateStr }));
            setPage(1);
          }
        });
      });
    }
    return () => {
      if (searchDatePickerRef.current) {
        searchDatePickerRef.current.destroy();
        searchDatePickerRef.current = null;
      }
    };
  }, []);

  // Update parent data when local state changes
  const updateOutpatientVisits = (newVisits) => {
    setLocalOutpatientVisits(newVisits);
    if (onDataUpdate) {
      onDataUpdate(newVisits);
    }
  };

  // Search outpatient visits using API
  const searchOutpatientVisits = async (searchParams = {}) => {
    try {
      setIsSearching(true);

      // Build search parameters with only non-empty values
      const params = {
        page: page,
        per_page: 5,
        clinic_id: clinicDataToUse?.clinic?.id,
      };

      // Only add parameters that have actual values
      if (searchParams.date_from) params.date_from = searchParams.date_from;
      if (searchParams.date_to) params.date_to = searchParams.date_to;
      if (searchParams.datetime_from) params.datetime_from = searchParams.datetime_from;
      if (searchParams.datetime_to) params.datetime_to = searchParams.datetime_to;
      if (searchParams.time_from) params.time_from = searchParams.time_from;
      if (searchParams.time_to) params.time_to = searchParams.time_to;
      if (searchParams.user_id) params.user_id = searchParams.user_id;
      if (searchParams.vet_id) params.vet_id = searchParams.vet_id;
      if (searchParams.pet_id) params.pet_id = searchParams.pet_id;
      if (searchParams.pet_owner_id) params.pet_owner_id = searchParams.pet_owner_id;
      if (searchParams.appointment_type_id) params.appointment_type_id = searchParams.appointment_type_id;
      if (searchParams.user_name) params.user_name = searchParams.user_name;
      if (searchParams.vet_name) params.vet_name = searchParams.vet_name;
      if (searchParams.pet_name) params.pet_name = searchParams.pet_name;
      if (searchParams.clinic_name) params.clinic_name = searchParams.clinic_name;
      if (searchParams.search) params.search = searchParams.search;
      if (searchParams.canceled_by) params.canceled_by = searchParams.canceled_by;
      if (searchParams.restored_by) params.restored_by = searchParams.restored_by;
      if (searchParams.has_canceled_reason) params.has_canceled_reason = searchParams.has_canceled_reason;
      if (searchParams.today) params.today = searchParams.today;
      if (searchParams.tomorrow) params.tomorrow = searchParams.tomorrow;
      if (searchParams.this_week) params.this_week = searchParams.this_week;
      if (searchParams.this_month) params.this_month = searchParams.this_month;
      if (searchParams.upcoming) params.upcoming = searchParams.upcoming;
      if (searchParams.past) params.past = searchParams.past;
      if (searchParams.sort_by) params.sort_by = searchParams.sort_by;
      if (searchParams.sort_order) params.sort_order = searchParams.sort_order;
      if (searchParams.active_only) params.active_only = 1;

      // Handle status string - only add if it has a value (ensure single value, not array)
      if (searchParams.status && searchParams.status !== '') {
        params.status = String(searchParams.status);
      }


      // For now, we'll use the clinic appointments API as a placeholder
      const response = await clinicAppointmentsAPI.searchOutpatientVisits(params);

      if (response.data && response.data.status === "success") {
        const visits = response.data.data.appointments || [];
        const pagination = response.data.data.pagination || {};
        const total = pagination.total || visits.length;

        // Transform visits to match the expected format
        const transformedVisits = visits.map(visit => {
          // Parse visit_date to extract date and time
          let date = '';
          let time = '';

          if (visit.visit_date) {
            // visit_date format: "2025-10-12 11:04:14"
            const dateTimeParts = visit.visit_date.split(' ');
            if (dateTimeParts.length >= 2) {
              date = dateTimeParts[0]; // "2025-10-12"
              const timePart = dateTimeParts[1]; // "11:04:14"
              if (timePart) {
                time = timePart.substring(0, 5); // "11:04"
              }
            }
          }

          return {
            id: visit.id,
            date: date,
            time: time,
            pet_owner: visit.pet_owner ?
              `${visit.pet_owner.first_name || ''} ${visit.pet_owner.last_name || ''}`.trim() :
              fallbackLabels.petGuardian,
            pet: visit.pet?.name || fallbackLabels.pet,
            vet: visit.vet ?
              `${visit.vet.first_name || ''} ${visit.vet.last_name || ''}`.trim() :
              fallbackLabels.vet,
            reason: visit.reason || visit.main_complaint || '',
            status: visit.status,
            type: 'outpatient',
            downpayment: visit.downpayment || 0,
            out_serial_num: visit.out_serial_num || 'N/A',
            originalData: visit
          };
        });

        setSearchResults(transformedVisits);
        setTotalSearchResults(total);
        setSearchPagination(pagination);

        // Update local visits for immediate UI feedback
        updateOutpatientVisits(transformedVisits);

        return transformedVisits;
      }

      return [];
    } catch (error) {
      console.error("Error searching outpatient visits:", error);
      const apiMessage = handleApiError ? handleApiError(error) : null;
      showErrorToast(apiMessage || t("receptionOutpatients.tab.toasts.searchFailed"));
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // Automatically trigger search for active visits on mount
  useEffect(() => {
    if (search.showAllActive && clinicDataToUse?.clinic?.id) {
      const searchParams = {
        ...search,
        active_only: 1,
        status: undefined
      };
      searchOutpatientVisits(searchParams);
    }
  }, [clinicDataToUse?.clinic?.id]);


  // Use search results if available, otherwise use local filtering
  const filteredRows = (searchResults.length > 0 ? searchResults : localOutpatientVisits).filter((row) => {
    // If using search results (API data)
    if (searchResults.length > 0) {
      // For "Show All..." modes, enforce status filtering on the client side
      // because sometimes the API might return mixed results or we want to be extra safe.
      if (search.showAllActive) {
        return !['completed', 'canceled', 'checkout', 'closed', 'cancelled'].includes((row.status || '').toLowerCase());
      }
      if (search.showAllCheckout) {
        return (row.status || '').toLowerCase() === 'checkout';
      }
      if (search.showAllClosed) {
        return (row.status || '').toLowerCase() === 'closed' || (row.status || '').toLowerCase() === 'completed';
      }
      // For other API searches (by name, date range, etc.), trust the API result
      return true;
    }

    // --- LOCAL FILTERING (for default view) ---

    // Convert row.date (YYYY-MM-DD) to DD-MM-YYYY for comparison with search.date (DD-MM-YYYY)
    const convertRowDateToDDMMYYYY = (dateStr) => {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert YYYY-MM-DD to DD-MM-YYYY
      }
      return dateStr;
    };

    const isRowActive = !['completed', 'canceled', 'checkout', 'closed', 'cancelled'].includes((row.status || '').toLowerCase());
    const isRowCheckout = (row.status || '').toLowerCase() === 'checkout';
    const isRowClosed = (row.status || '').toLowerCase() === 'closed' || (row.status || '').toLowerCase() === 'completed';

    let passesDateFilter = true;
    // Note: Local filtering for "Show All..." uses the same logic, although usually these modes trigger API search.
    // We keep this here in case fallback to local data happens or logic changes.
    if (search.showAllActive) {
      passesDateFilter = isRowActive;
    } else if (search.showAllCheckout) {
      passesDateFilter = isRowCheckout;
    } else if (search.showAllClosed) {
      passesDateFilter = isRowClosed;
    } else {
      // Normal mode: check date match
      passesDateFilter = !search.date || convertRowDateToDDMMYYYY(row.date) === search.date;
    }

    const passesPetOwnerFilter = !search.pet_owner ||
      row.pet_owner.toLowerCase().includes(search.pet_owner.toLowerCase());
    const passesPetFilter = !search.pet ||
      (typeof row.pet === 'string' ? row.pet.toLowerCase().includes(search.pet.toLowerCase()) :
        row.pet.name.toLowerCase().includes(search.pet.toLowerCase()));
    const passesVetFilter = !search.vet ||
      row.vet.toLowerCase().includes(search.vet.toLowerCase());

    const passesStatusFilter = !search.status || (row.status && row.status.toLowerCase() === search.status.toLowerCase());
    return passesDateFilter && passesPetOwnerFilter && passesPetFilter && passesVetFilter && passesStatusFilter;
  });

  // Pagination - use search results if available, otherwise use local filtering
  const totalPages = searchResults.length > 0
    ? (searchPagination?.last_page || 1)
    : Math.ceil(filteredRows.length / ROWS_PER_PAGE);

  const paginatedRows = searchResults.length > 0
    ? filteredRows // Search results are already paginated by the API
    : filteredRows.slice(
      (page - 1) * ROWS_PER_PAGE,
      page * ROWS_PER_PAGE
    );

  // Use filtered count for display to match what the user actually sees on the screen,
  // particularly when client-side filtering is applied on top of API results.
  // If there are multiple pages, we fall back to totalSearchResults from the API.
  const displayedCount = (searchResults.length > 0 && totalPages > 1) ? totalSearchResults : filteredRows.length;
  const countKey = searchResults.length > 0
    ? "receptionOutpatients.tab.header.searchCount"
    : "receptionOutpatients.tab.header.count";
  const countLabel = t(countKey, { count: displayedCount });










  // Handle tooltip menu actions
  const handleTooltipAction = async (action, row) => {
    switch (action) {
      case 'edit':
        // Handle edit outpatient visit
        setEditOutpatientData(row);
        setShowEditOutpatientModal(true);
        break;
      case 'checkout':
        // Show detailed overlay for checkout
        setSelectedOutpatientId(row.id);
        setShowOverlay(true);
        break;
      case 'close':
        // Ask for confirmation before closing outpatient visit
        setRowToClose(row);
        setShowCloseConfirmModal(true);
        break;
      case 'admit_inpatient':
        // Handle admit as inpatient
        break;
      case 'convert_inpatient':
        // Handle convert to inpatient
        setRowToConvert(row);
        setShowConvertConfirmModal(true);
        break;
      default:
        break;
    }
  };

  // Handle overlay close
  const handleOverlayClose = useCallback(() => {
    setShowOverlay(false);
    setSelectedOutpatientId(null);
    if (onDetailsOpenChange) {
      // When overlay closes, also clear SOAP state
      onDetailsOpenChange(false, null, false, null);
    }
  }, [onDetailsOpenChange]);

  // Notify parent when overlay opens/closes (SOAP state is handled separately via onSOAPOpenChange)
  useEffect(() => {
    if (onDetailsOpenChange) {
      // Only notify about overlay state, don't pass SOAP state (undefined means don't update SOAP state)
      onDetailsOpenChange(showOverlay, showOverlay ? handleOverlayClose : null, undefined, undefined);
    }
  }, [showOverlay, onDetailsOpenChange, handleOverlayClose]);

  // Hide help tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showHelpTooltip && !event.target.closest('tr')) {
        setShowHelpTooltip(false);
      }
    };

    if (showHelpTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showHelpTooltip]);

  useEffect(() => {
    setSearch((prev) => ({
      ...prev,
      date: getDefaultDateString(),
    }));
    if (searchDatePickerRef.current) {
      searchDatePickerRef.current.setDate(new Date());
    }
  }, [getDefaultDateString]);

  return (
    <div className="reception-tab-content" dir={i18n.dir()} lang={i18n.language}>
      <style>{`
        .outpatient-card .action-buttons .action-btn {
          min-width: 120px !important;
          padding: 8px 16px !important;
          font-size: 0.85rem !important;
          border-radius: 6px !important;
        }
      `}</style>
      {/* Search Section - Hidden when details view is open */}
      {!showOverlay && (
        <div className="reception-search-box">
          <div className="search-input-group">
            <label className="search-label">{t("receptionOutpatients.tab.search.date")}</label>
            <input
              ref={searchDateInputRef}
              type="text"
              className="reception-input"
              placeholder={t("receptionOutpatients.tab.search.datePlaceholder")}
              value={search.date}
              disabled={search.showAllActive || search.showAllCheckout || search.showAllClosed}
              style={{ opacity: (search.showAllActive || search.showAllCheckout || search.showAllClosed) ? 0.5 : 1, cursor: (search.showAllActive || search.showAllCheckout || search.showAllClosed) ? 'not-allowed' : 'pointer' }}
              readOnly
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                type="button"
                className="quick-filter-btn"
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: search.showAllActive ? '#00d4b3' : '#f9fafb',
                  color: search.showAllActive ? '#fff' : '#6b7280',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  flex: 1,
                  transition: 'all 0.2s ease',
                  boxShadow: search.showAllActive ? '0 1px 3px rgba(0, 212, 179, 0.2)' : 'none'
                }}
                onClick={async () => {
                  const isActive = !search.showAllActive;
                  setSearch(s => ({
                    ...s,
                    showAllActive: isActive,
                    showAllCheckout: false,
                    showAllClosed: false
                  }));
                  setPage(1);

                  if (isActive) {
                    const searchParams = {
                      ...search,
                      active_only: true,
                      status: undefined
                    };
                    await searchOutpatientVisits(searchParams);
                  } else {
                    setSearchResults([]);
                    setTotalSearchResults(0);
                    setSearchPagination(null);
                  }
                }}
              >
                {t("receptionOutpatients.tab.search.showAllActive", { defaultValue: "Show All Active" })}
              </button>
              {userType === USER_TYPES.CLINIC_MODERATOR && (
                <>
                  <button
                    type="button"
                    className="quick-filter-btn"
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: search.showAllCheckout ? '#00d4b3' : '#f9fafb',
                      color: search.showAllCheckout ? '#fff' : '#6b7280',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      flex: 1,
                      transition: 'all 0.2s ease',
                      boxShadow: search.showAllCheckout ? '0 1px 3px rgba(0, 212, 179, 0.2)' : 'none'
                    }}
                    onClick={async () => {
                      const isActive = !search.showAllCheckout;
                      setSearch(s => ({
                        ...s,
                        showAllCheckout: isActive,
                        showAllActive: false,
                        showAllClosed: false
                      }));
                      setPage(1);

                      if (isActive) {
                        const searchParams = {
                          ...search,
                          status: 'checkout'
                        };
                        await searchOutpatientVisits(searchParams);
                      } else {
                        setSearchResults([]);
                        setTotalSearchResults(0);
                        setSearchPagination(null);
                      }
                    }}
                  >
                    {t("receptionOutpatients.tab.search.showAllCheckout", { defaultValue: "Show All Checkout" })}
                  </button>
                  <button
                    type="button"
                    className="quick-filter-btn"
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: search.showAllClosed ? '#00d4b3' : '#f9fafb',
                      color: search.showAllClosed ? '#fff' : '#6b7280',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      flex: 1,
                      transition: 'all 0.2s ease',
                      boxShadow: search.showAllClosed ? '0 1px 3px rgba(0, 212, 179, 0.2)' : 'none'
                    }}
                    onClick={async () => {
                      const isActive = !search.showAllClosed;
                      setSearch(s => ({
                        ...s,
                        showAllClosed: isActive,
                        showAllActive: false,
                        showAllCheckout: false
                      }));
                      setPage(1);

                      if (isActive) {
                        const searchParams = {
                          ...search,
                          status: 'completed'
                        };
                        await searchOutpatientVisits(searchParams);
                      } else {
                        setSearchResults([]);
                        setTotalSearchResults(0);
                        setSearchPagination(null);
                      }
                    }}
                  >
                    {t("receptionOutpatients.tab.search.showAllClosed", { defaultValue: "Show All Closed" })}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="search-input-group">
            <label className="search-label">{t("receptionOutpatients.tab.search.petGuardian")}</label>
            <input
              type="text"
              placeholder={t("receptionOutpatients.tab.search.petGuardianPlaceholder")}
              className="reception-input"
              value={search.pet_owner}
              onChange={(e) => {
                setSearch((s) => ({ ...s, pet_owner: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">{t("receptionOutpatients.tab.search.petName")}</label>
            <input
              type="text"
              placeholder={t("receptionOutpatients.tab.search.petNamePlaceholder")}
              className="reception-input"
              value={search.pet}
              onChange={(e) => {
                setSearch((s) => ({ ...s, pet: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">{t("receptionOutpatients.tab.search.vet")}</label>
            <input
              type="text"
              placeholder={t("receptionOutpatients.tab.search.vetPlaceholder")}
              className="reception-input"
              value={search.vet}
              onChange={(e) => {
                setSearch((s) => ({ ...s, vet: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">{t("receptionOutpatients.tab.search.status")}</label>
            <select
              className="reception-input"
              value={search.status || ""}
              onChange={(e) => {
                const value = e.target.value;
                setSearch((s) => ({ ...s, status: value }));
                setPage(1);
              }}
            >
              <option value="">{t("receptionOutpatients.tab.search.statusOptions.all")}</option>
              <option value="pending">{t("receptionOutpatients.tab.status.pending")}</option>
              <option value="confirmed">{t("receptionOutpatients.tab.status.confirmed")}</option>
              <option value="completed">{t("receptionOutpatients.tab.status.completed")}</option>
              <option value="canceled">{t("receptionOutpatients.tab.status.canceled")}</option>
              <option value="checkout">{t("receptionOutpatients.tab.status.checkout")}</option>
            </select>
          </div>
          <div className="search-input-group">
            <label className="search-label">&nbsp;</label>
            <input
              type="button"
              className="reception-search-btn reception-input"
              value={isSearching ? t("receptionOutpatients.tab.search.searching") : t("receptionOutpatients.tab.search.button")}
              disabled={isSearching}
              onClick={async (e) => {
                e.preventDefault();
                setPage(1);

                // Convert DD-MM-YYYY to YYYY-MM-DD for API
                const convertDateFormat = (dateStr) => {
                  if (!dateStr) return '';
                  const parts = dateStr.split('-');
                  if (parts.length === 3) {
                    return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert DD-MM-YYYY to YYYY-MM-DD
                  }
                  return dateStr;
                };

                // Map basic search fields to API parameters
                const searchParams = {
                  ...search,
                  // Override with specific datetime parameters for date search
                  date_from: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? convertDateFormat(search.date) : '',
                  date_to: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? convertDateFormat(search.date) : '',
                  user_name: search.pet_owner || search.user_name,
                  vet_name: search.vet || search.vet_name,
                  pet_name: search.pet || search.pet_name,
                  search: search.pet_owner || search.pet || search.vet || search.date || search.search,
                  // Ensure status is sent as single value, not array
                  // For closed, we search for 'completed' since closing sets status to 'completed'
                  status: search.showAllCheckout ? 'checkout' : search.showAllClosed ? 'completed' : search.status || undefined,
                  active_only: search.showAllActive ? 1 : undefined
                };

                await searchOutpatientVisits(searchParams);
              }}
            />
          </div>
        </div>
      )}

      {/* Cards Section OR Details View */}
      {showOverlay ? (
        <OutpatientDetailsOverlay
          isVisible={showOverlay}
          outpatientId={selectedOutpatientId}
          onClose={handleOverlayClose}
          workingHours={clinicDataToUse?.clinic?.working_hours || []}
          fetchAllTabData={fetchAllTabData}
          onSOAPOpenChange={(isSOAPOpen, closeSOAPHandler) => {
            if (onDetailsOpenChange) {
              // Explicitly pass SOAP state - this is the authoritative source for SOAP state
              onDetailsOpenChange(showOverlay, showOverlay ? handleOverlayClose : null, isSOAPOpen, closeSOAPHandler);
            }
          }}
        />
      ) : (
        <div className="reception-cards-wrapper">
          <div className="cards-header">
            <div className="cards-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="cards-title-left">
                {t("receptionOutpatients.tab.header.title")}
                <span className="cards-count">
                  ({countLabel})
                  {searchResults.length > 0 && (
                    <span style={{ color: '#059669', marginLeft: '8px' }}>
                      🔍 {t("receptionOutpatients.tab.header.searchTag")}
                    </span>
                  )}
                </span>
              </div>
              <div className="cards-title-right">
                {searchResults.length > 0 && (
                  <button
                    className="reception-action-btn"
                    style={{ marginRight: '8px', backgroundColor: '#6b7280' }}
                    onClick={() => {
                      // Clear search results
                      setSearchResults([]);
                      setTotalSearchResults(0);
                      setSearchPagination(null);
                      setPage(1);

                      // Reset all search filters
                      setSearch({
                        // Basic search fields
                        date: getDefaultDateString(),
                        pet_owner: "",
                        pet: "",
                        vet: "",

                        // Advanced search parameters
                        user_id: "",
                        vet_id: "",
                        clinic_id: "",
                        pet_id: "",
                        pet_owner_id: "",
                        appointment_type_id: "",
                        status: "",
                        date_from: "",
                        date_to: "",
                        datetime_from: "",
                        datetime_to: "",
                        time_from: "",
                        time_to: "",
                        search: "",
                        user_name: "",
                        vet_name: "",
                        pet_name: "",
                        clinic_name: "",
                        canceled_by: "",
                        restored_by: "",
                        has_canceled_reason: false,
                        today: false,
                        tomorrow: false,
                        this_week: false,
                        this_month: false,
                        upcoming: false,
                        past: false,
                        sort_by: "appointment_at",
                        sort_order: "desc",
                        per_page: 5,
                        showAllActive: true,
                        showAllCheckout: false,
                        showAllClosed: false
                      });

                      // Reset date picker to current date if it exists
                      if (searchDatePickerRef.current) {
                        searchDatePickerRef.current.setDate(new Date());
                      }
                    }}
                  >
                    {t("receptionOutpatients.tab.search.clear")}
                  </button>
                )}
                <button
                  className="reception-action-btn"
                  onClick={() => {
                    if (!isFreePlan) {
                      setShowPatientChooser(true);
                    }
                  }}
                  disabled={isFreePlan}
                  style={{
                    position: 'relative',
                    opacity: isFreePlan ? 0.6 : 1,
                    cursor: isFreePlan ? 'not-allowed' : 'pointer'
                  }}
                  title={isFreePlan ? "Feature Locked, Upgrade to Unlock" : undefined}
                >
                  {t("receptionOutpatients.tab.buttons.new")}
                  {isFreePlan && (
                    <span
                      className="feature-lock-icon-small"
                      style={{
                        marginLeft: '8px',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                      title="Feature Locked, Upgrade to Unlock"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {loadingLists || isSearching ? (
            <div className="cards-loading-state">
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            </div>
          ) : paginatedRows.length === 0 ? (
            <div className="cards-empty-state">
              <div className="empty-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                <h4>{t("receptionOutpatients.tab.empty.title")}</h4>
                <p>{t("receptionOutpatients.tab.empty.description")}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="outpatient-cards-grid">
                {paginatedRows.map((row, idx) => {
                  const statusKey = (row.status || "").toLowerCase();
                  return (
                    <div
                      key={idx + (page - 1) * ROWS_PER_PAGE}
                      data-card-id={row.id}
                      className="outpatient-card"
                    >
                      {/* Modern Card Design */}
                      <div className="modern-card">
                        {/* Top Section - Pet & Status */}
                        <div className="card-top-section">
                          <div className="pet-section">
                            <div className="pet-avatar">
                              <img
                                src={getPetImageUrl(row)}
                                alt={typeof row.pet === 'string' ? row.pet : (row.pet?.name || fallbackLabels.pet)}
                                className="avatar-image"
                                onError={(e) => {
                                  e.target.src = "/images/no-image-pet.png";
                                }}
                              />
                            </div>
                            <div className="pet-info">
                              <h3 className="pet-name">
                                {typeof row.pet === 'string' ? row.pet : (row.pet?.name || fallbackLabels.pet)}
                              </h3>
                              <p className="pet-owner">
                                {t("receptionOutpatients.tab.cards.owner", {
                                  owner: row.pet_owner || fallbackLabels.petGuardian,
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="status-section">
                            <span className={`status-badge ${statusKey}`}>
                              {formatStatus(row.status)}
                            </span>
                          </div>
                        </div>

                        {/* Middle Section - Visit Details */}
                        <div className="card-middle-section">
                          <div className="visit-info">
                            <div className="info-row">
                              <div className="info-item">
                                <div className="info-icon">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                  </svg>
                                </div>
                                <span className="info-text">{row.date}</span>
                              </div>
                              <div className="info-item">
                                <div className="info-icon">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                  </svg>
                                </div>
                                <span className="info-text">
                                  {row.vet || fallbackLabels.vet}
                                </span>
                              </div>
                            </div>
                            {row.out_serial_num && (
                              <div className="info-row" style={{ marginTop: '8px', display: 'flex' }}>
                                <div className="info-item" style={{ width: '100%', display: 'flex', alignItems: 'flex-start' }}>
                                  <span className="info-text serial" style={{
                                    color: '#6B7280',
                                    fontSize: '13px',
                                    fontFamily: 'monospace',
                                    width: '100%',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {t("receptionOutpatients.tab.cards.serial", { number: row.out_serial_num, defaultValue: `Visit ID: ${row.out_serial_num}` })}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Visit Reason */}
                          <div className="card-reason-section" style={{ textAlign: 'left', marginTop: '12px', padding: 0, border: 'none' }}>
                            <p className="visit-reason" style={{ textAlign: 'left', justifyContent: 'flex-start' }}>
                              <span style={{ fontWeight: '600', color: '#374151' }}>
                                {t("receptionOutpatients.tab.cards.reasonLabel")}{" "}
                              </span>
                              {formatReason(row.reason, row.vet)}
                            </p>
                          </div>
                        </div>

                        {/* Bottom Section - Actions */}
                        <div className="card-bottom-section">
                          <div className="action-buttons">
                            {userType !== USER_TYPES.CLINIC_MODERATOR && (
                              <button
                                className="action-btn primary"
                                style={{ minWidth: '120px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTooltipAction('checkout', row);
                                }}
                              >
                                <i className="fas fa-eye"></i>
                                {t("receptionOutpatients.tab.cards.buttons.details")}
                              </button>
                            )}
                            {((row.status || '').toLowerCase() !== 'checkout' && (row.status || '').toLowerCase() !== 'completed') && (
                              <button
                                className="action-btn secondary"
                                style={{ minWidth: '120px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTooltipAction('edit', row);
                                }}
                                title={t("receptionOutpatients.tab.cards.buttons.editTooltip")}
                              >
                                <i className="fas fa-edit"></i>
                                {t("receptionOutpatients.tab.cards.buttons.edit")}
                              </button>
                            )}
                            {row.status === 'checkout' && userType === USER_TYPES.CLINIC_MODERATOR && (
                              <button
                                className="action-btn close"
                                style={{ minWidth: '120px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTooltipAction('close', row);
                                }}
                                title={t("receptionOutpatients.tab.cards.buttons.closeTooltip")}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                {t("receptionOutpatients.tab.cards.buttons.close")}
                              </button>
                            )}

                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {
                totalPages > 1 && (
                  <div className="reception-pagination">
                    <button
                      className="reception-action-btn"
                      onClick={async () => {
                        const newPage = Math.max(1, page - 1);
                        setPage(newPage);

                        // If we have search results, trigger a new search with the new page
                        if (searchResults.length > 0) {
                          // Convert DD-MM-YYYY to YYYY-MM-DD for API
                          const convertDateFormat = (dateStr) => {
                            if (!dateStr) return '';
                            const parts = dateStr.split('-');
                            if (parts.length === 3) {
                              return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert DD-MM-YYYY to YYYY-MM-DD
                            }
                            return dateStr;
                          };

                          const searchParams = {
                            ...search,
                            datetime_from: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? `${convertDateFormat(search.date)} 00:00:00` : '',
                            datetime_to: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? `${convertDateFormat(search.date)} 23:59:59` : '',
                            user_name: search.pet_owner || search.user_name,
                            vet_name: search.vet || search.vet_name,
                            pet_name: search.pet || search.pet_name,
                            search: search.pet_owner || search.pet || search.vet || search.date || search.search,
                            status: search.showAllCheckout ? 'checkout' : search.showAllClosed ? 'closed' : search.status || undefined,
                            active_only: search.showAllActive ? 1 : undefined,
                            page: newPage
                          };
                          await searchOutpatientVisits(searchParams);
                        }
                      }}
                      disabled={page === 1 || isSearching}
                    >
                      {t("receptionOutpatients.tab.pagination.previous")}
                    </button>
                    <span>
                      {t("receptionOutpatients.tab.pagination.pageCount", {
                        current: page,
                        total: totalPages,
                      })}
                    </span>
                    <button
                      className="reception-action-btn"
                      onClick={async () => {
                        const newPage = Math.min(totalPages, page + 1);
                        setPage(newPage);

                        // If we have search results, trigger a new search with the new page
                        if (searchResults.length > 0) {
                          // Convert DD-MM-YYYY to YYYY-MM-DD for API
                          const convertDateFormat = (dateStr) => {
                            if (!dateStr) return '';
                            const parts = dateStr.split('-');
                            if (parts.length === 3) {
                              return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert DD-MM-YYYY to YYYY-MM-DD
                            }
                            return dateStr;
                          };

                          const searchParams = {
                            ...search,
                            datetime_from: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? `${convertDateFormat(search.date)} 00:00:00` : '',
                            datetime_to: (search.date && !search.showAllActive && !search.showAllCheckout && !search.showAllClosed) ? `${convertDateFormat(search.date)} 23:59:59` : '',
                            user_name: search.pet_owner || search.user_name,
                            vet_name: search.vet || search.vet_name,
                            pet_name: search.pet || search.pet_name,
                            search: search.pet_owner || search.pet || search.vet || search.date || search.search,
                            status: search.showAllCheckout ? 'checkout' : search.showAllClosed ? 'closed' : search.status || undefined,
                            active_only: search.showAllActive ? 1 : undefined,
                            page: newPage
                          };
                          await searchOutpatientVisits(searchParams);
                        }
                      }}
                      disabled={page === totalPages || isSearching}
                    >
                      {t("receptionOutpatients.tab.pagination.next")}
                    </button>
                  </div>
                )
              }
            </>
          )
          }
        </div>
      )}




      {/* Action Buttons */}
      {/* <div className="reception-actions">
        <button
          className="reception-action-btn"
          onClick={() => {
            setShowAdmitOutpatientModal(true);
          }}
        >
          Admit New Outpatient
        </button>
        <button className="reception-action-btn" disabled>
          Checkout
        </button>
        <button className="reception-action-btn">
          Admit Inpatient
        </button>
      </div> */}

      {/* Patient Type Chooser Modal */}
      <PatientTypeChooserModal
        isOpen={showPatientChooser}
        onClose={() => setShowPatientChooser(false)}
        visitType="outpatient"
        onExistingPatient={() => {
          setPrefillPetOwner(null);
          setPrefillPet(null);
          setShowAdmitOutpatientModal(true);
        }}
        onNewPatient={() => {
          setShowNewPatientWizard(true);
        }}
      />

      {/* New Patient Wizard Modal */}
      <NewPatientWizardModal
        isOpen={showNewPatientWizard}
        onClose={() => setShowNewPatientWizard(false)}
        visitType="outpatient"
        onComplete={(user, pet) => {
          setPrefillPetOwner(user);
          setPrefillPet(pet);
          setShowAdmitOutpatientModal(true);
        }}
      />

      {/* Admit New Outpatient Modal */}
      <AdmitOutpatientModal
        isOpen={showAdmitOutpatientModal}
        onClose={() => {
          setShowAdmitOutpatientModal(false);
          setPrefillPetOwner(null);
          setPrefillPet(null);
        }}
        onSave={() => {
          fetchAllTabData(true);
        }}
        vets={vets}
        inpatientVisits={inpatientVisits}
        outpatientVisits={outpatientVisits}
        workingHours={clinicDataToUse?.clinic?.working_hours || []}
        appointmentTypes={clinicDataToUse?.appointment_types || []}
        prefillPetOwner={prefillPetOwner}
        prefillPet={prefillPet}
      />

      {/* Edit Outpatient Modal */}
      <EditOutpatientModal
        isOpen={showEditOutpatientModal}
        onClose={() => {
          setShowEditOutpatientModal(false);
          setEditOutpatientData(null);
        }}
        onSave={(updatedOutpatientData) => {
          const updatedVisits = localOutpatientVisits.map((visit) =>
            visit.id === updatedOutpatientData.id
              ? { ...visit, ...updatedOutpatientData }
              : visit
          );
          updateOutpatientVisits(updatedVisits);
          fetchAllTabData(true);
          setShowEditOutpatientModal(false);
          setEditOutpatientData(null);
        }}
        vets={vets}
        workingHours={clinicDataToUse?.clinic?.working_hours || []}
        appointmentTypes={clinicDataToUse?.appointment_types || []}
        editData={editOutpatientData}
      />


      {/* Help Tooltip */}
      {showHelpTooltip && (
        <div
          className="help-tooltip"
          style={{
            position: 'fixed',
            top: helpTooltipPosition.y - 40,
            left: helpTooltipPosition.x - 100,
            zIndex: 1001,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none',
            animation: 'fadeInUp 0.2s ease-out',
            maxWidth: '200px',
            textAlign: 'center'
          }}
        >
          💡 {t("receptionOutpatients.tab.helpTooltip")}
        </div>
      )}


      {/* Close Visit Modal */}
      <CloseOutpatientModal
        isOpen={showCloseConfirmModal}
        onClose={() => {
          setShowCloseConfirmModal(false);
          setRowToClose(null);
        }}
        outpatientId={rowToClose?.id}
        paymentAmount={parseFloat(rowToCloseFromSource?.remaining_amount) || 0}
        totalAmount={parseFloat(rowToCloseFromSource?.originalData?.total_amount) || 0}
        downpayment={parseFloat(rowToCloseFromSource?.originalData?.downpayment) || parseFloat(rowToCloseFromSource?.downpayment) || 0}
        onSuccess={() => {
          showSuccessToast(t("receptionOutpatients.tab.toasts.closeSuccess"));

          // Update local state to remove the closed visit
          setLocalOutpatientVisits(prev => prev.filter(visit => visit.id !== rowToClose.id));

          // Refresh data
          if (fetchAllTabData) {
            fetchAllTabData();
          }

          setShowCloseConfirmModal(false);
          setRowToClose(null);
        }}
      />

      {/* Convert to Inpatient - Confirmation Modal */}
      <ConvertToInpatientModal
        isOpen={showConvertConfirmModal}
        onClose={() => {
          setShowConvertConfirmModal(false);
          setRowToConvert(null);
        }}
        visitData={rowToConvert}
        isConverting={Boolean(convertingVisitId)}
        onConvert={async (visitId) => {
          setConvertingVisitId(visitId);
        }}
        onSuccess={(visitData) => {
          // Update local state to remove the converted visit
          setLocalOutpatientVisits(prev => prev.filter(visit => visit.id !== visitData.id));

          // Refresh data
          if (fetchAllTabData) {
            fetchAllTabData();
          }

          setConvertingVisitId(null);
          setShowConvertConfirmModal(false);
          setRowToConvert(null);
        }}
        onError={() => {
          setConvertingVisitId(null);
        }}
      />
    </div>
  );
};

export default OutpatientTab;
